/* eslint-disable react/prop-types */
/**
 * Error boundary to prevent uncaught errors from unmounting the entire tree.
 *
 * This component should be placed around major pieces of functionality to
 * limit the blast radius of an exception.
 */

import React, { Fragment, useState, useEffect } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import Modal, {
	ModalTransition,
	ModalHeader,
	ModalTitle,
	ModalBody,
	ModalFooter,
} from '@atlaskit/modal-dialog';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import Button from '@atlaskit/button/standard-button';

import { getReactErrorAttributes } from '@confluence/monitoring';

import { AtlassianSupportLink } from './AtlassianSupportLink';
import type { ErrorComponentProps } from './GenericErrorBoundary';
import { DevErrorDetail } from './DevErrorDetail';

export const i18n = defineMessages({
	actionCloseModal: {
		id: 'error-boundary.close',
		description: 'Button to close the dialog',
		defaultMessage: 'Close',
	},
	actionReloadPage: {
		id: 'error-boundary.reload-page',
		description: 'The button/link which triggers a refresh/reload of the current page',
		defaultMessage: 'Refresh page',
	},

	description: {
		id: 'error-boundary.description',
		description:
			"Additional text shown when displaying a technical error. {atlassianSupportLink} will be substituted with a message that has id='error-boundary.support-link-text' which reads as 'our support team'. This phrase continues with a list of bullet points",
		defaultMessage:
			'The issue should resolve on its own, but if it keeps happening, ask your admin to contact {atlassianSupportLink} and give them:',
	},

	title: {
		id: 'error-boundary.title',
		description: 'Title shown when displaying a technical error',
		defaultMessage: 'We’ve hit a snag',
	},

	editorTitle: {
		id: 'error-boundary.editor.title',
		description: 'Title shown when displaying a technical error in the editor',
		defaultMessage: 'Content failed to load',
	},

	code: {
		id: 'error-boundary.code',
		description:
			'The code field in error boundary component {code} will be substituted with a string of digits and letters.',
		defaultMessage: 'The code associated with this error: {code}',
	},

	url: {
		id: 'error-boundary.url',
		description: 'The url field in error boundary component',
		defaultMessage: 'The URL of this page',
	},

	refresh: {
		id: 'error-boundary.refresh',
		description:
			"The refresh field in error boundary component. {refreshLink} will be substituted with a message that has id='error-boundary.description.refresh' which reads as 'Refresh'",
		defaultMessage: 'Try refreshing the page. {refreshLink}',
	},

	refreshLink: {
		id: 'error-boundary.description.refresh-link',
		description: 'The text of the link which will help user refresh the page',
		defaultMessage: 'Refresh',
	},

	supportLinkText: {
		id: 'error-boundary.support-link-text',
		description: 'The text of the link which will lead the user to the Atlassian Support web site',
		defaultMessage: 'our support team',
	},
});

export type ErrorComponentModalProps = {
	modal?:
		| boolean
		| {
				closeText?: string;
				onClose?(): void;
		  };
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NonModalContainer = styled.div({
	display: 'flex',
	justifyContent: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionMessageContainer = styled.div({
	maxWidth: '600px',
});

export const ErrorComponent = ({
	attribution,
	error,
	errorInfo,
	customTitle,
	modal,
}: ErrorComponentProps & ErrorComponentModalProps) => {
	const intl = useIntl();
	const [isModalOpen, setModalOpen] = useState(true);
	const [showDevErrorDetail, setShowDevErrorDetail] = useState(false);

	useEffect(() => {
		focusError();
	}, []);

	const focusError = () => {
		const error = document.querySelector(`#error-container`);
		return (error as HTMLElement)?.focus();
	};

	let title;
	if (customTitle) {
		title = customTitle;
	} else if (attribution === 'fabric') {
		title = intl.formatMessage(i18n.editorTitle);
	} else {
		title = intl.formatMessage(i18n.title);
	}

	let errorDetail = <ErrorDetail {...{ attribution, error, errorInfo }} />;
	const actions: [
		{
			key: string;
			text: string;
			onClick: () => any;
			appearance: 'danger' | 'subtle';
			autoFocus?: boolean;
		},
	] = [
		{
			key: 'error-boundary.reload-page',
			text: intl.formatMessage(i18n.actionReloadPage),
			appearance: 'danger',
			autoFocus: true,
			onClick() {
				window.location.reload();
			},
		},
	];

	if (process.env.NODE_ENV !== 'production') {
		actions.push({
			key: 'error-boundary.show-dev-error-details',
			appearance: 'subtle',
			text: `${showDevErrorDetail ? 'Hide' : 'Show'} dev-only details`,
			onClick() {
				setShowDevErrorDetail(!showDevErrorDetail);
			},
		});
	}
	if (showDevErrorDetail) {
		errorDetail = (
			<Fragment>
				{errorDetail}
				<DevErrorDetail {...{ attribution, error, errorInfo }} />
			</Fragment>
		);
	}

	if (modal) {
		const onModalClose = () => setModalOpen(false);
		const modalActions = [...actions];

		// when content failed to load in editor
		// we do not want to allow user to close the error modal
		// because it will just lead them to empty page
		if (attribution !== 'fabric') {
			modalActions.push({
				key: 'error-boundary.close-modal',
				appearance: 'subtle',
				text:
					(typeof modal === 'object' && modal.closeText) ||
					intl.formatMessage(i18n.actionCloseModal),
				onClick: (typeof modal === 'object' && modal.onClose) || onModalClose,
			});
		}

		/* eslint-disable jsx-a11y/no-autofocus */
		return (
			<ModalTransition>
				{isModalOpen ? (
					<Modal onClose={onModalClose} autoFocus={false}>
						<ModalHeader>
							<ModalTitle appearance="danger">{title}</ModalTitle>
						</ModalHeader>
						<ModalBody>{errorDetail}</ModalBody>
						<ModalFooter>
							{modalActions.map(({ onClick, appearance, autoFocus, key, text }) => (
								<Button autoFocus={autoFocus} appearance={appearance} onClick={onClick} key={key}>
									{text}
								</Button>
							))}
						</ModalFooter>
					</Modal>
				) : null}
			</ModalTransition>
		);
	}

	return (
		<NonModalContainer>
			<SectionMessageContainer id="error-container" tabIndex={0}>
				<SectionMessage
					appearance="error"
					actions={actions.map(({ onClick, key, text }) => (
						<SectionMessageAction onClick={onClick} key={key}>
							{text}
						</SectionMessageAction>
					))}
					title={title}
				>
					{errorDetail}
				</SectionMessage>
			</SectionMessageContainer>
		</NonModalContainer>
	);
};

function ErrorDetail(props: ErrorComponentProps) {
	const reactErrorAttributes = getReactErrorAttributes(props.error);
	const errorHashCode = reactErrorAttributes ? reactErrorAttributes.errorHashCode : undefined;

	return (
		<Fragment>
			<FormattedMessage
				{...i18n.description}
				tagName="p"
				values={{
					atlassianSupportLink: (
						<AtlassianSupportLink>
							<FormattedMessage {...i18n.supportLinkText} />
						</AtlassianSupportLink>
					),
				}}
			/>

			<ul>
				<li>
					<FormattedMessage {...i18n.url} />
				</li>
				{errorHashCode ? (
					<li>
						<FormattedMessage {...i18n.code} values={{ code: <b>{errorHashCode || ''}</b> }} />
					</li>
				) : null}
			</ul>
		</Fragment>
	);
}
